'use client'

import { X } from 'lucide-react'

import { GroupIconAction, List } from '~/components/component-library'
import { StepDone } from '~/components/post-onboarding-checklist/StepDone'
import { WorkspaceAutoEarnStepAction } from '~/components/post-onboarding-checklist/WorkspaceAutoEarnStepAction'
import {
  getTeammateStepMeta,
  WAITING_FOR_INVITATION,
} from '~/components/post-onboarding-checklist/teammateStep'
import { isChecklistStepComplete } from '~/components/post-onboarding-checklist/utils'
import { InviteMemberDialogContent } from '~/components/settings/InviteMemberDialogContent'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgMember } from '~/hooks/org'
import { usePostOnboardingChecklistUpdates } from '~/hooks/usePostOnboardingChecklist'
import { useServerRequests } from '~/hooks/useRequests'
import {
  PostOnboardingChecklist,
  PostOnboardingChecklistStepKey,
} from '~/types'

// Slots align to the row top. A small button's height on each lines them up.
const StepLabel = ({ children }: { children: string }) => (
  <span className="flex min-h-8 items-center">{children}</span>
)

const StepMeta = ({ children }: { children: string }) => (
  <span className="flex h-8 items-center text-sm">{children}</span>
)

const getTeammateStepLabel = ({
  isTeammateAdded,
  hasPendingInvitation,
}: {
  isTeammateAdded: boolean
  hasPendingInvitation: boolean
}) => {
  if (isTeammateAdded) return 'Teammate added'
  if (hasPendingInvitation) return 'Invitation sent'
  return 'Add a teammate'
}

// The checklist in the dashboard's right sidebar once the org is funded.
// Dismissing is the single user control and is per-user — it never changes
// what other members see. Once every step is done it shows a success message
// in place of the list.
export const PostOnboardingChecklistPanel = ({
  checklist,
}: {
  checklist: PostOnboardingChecklist
}) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { org } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { setPostOnboardingChecklist } = usePostOnboardingChecklistUpdates()

  const isFunded = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.Fund,
  )
  const isTeammateAdded = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.AddTeammate,
  )
  const isAutoEarnOn = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.AutoEarn,
  )
  const hasPendingInvitation = !isTeammateAdded && !!org?.invitations.length

  const openInviteDialog = () => {
    setGlobalModalState({
      isOpen: true,
      content: <InviteMemberDialogContent closeDialog={closeGlobalModal} />,
    })
  }

  const handleHide = async () => {
    const result = await serverPostRequest({
      path: '/api/org/post_onboarding_checklist/hide',
      params: { orgId: checklist.orgId },
      errorMessage: 'Failed to hide the checklist',
    })
    // The endpoint sends no body. Hiding is per user, thus the flag is the
    // only field that changes.
    if (result) setPostOnboardingChecklist({ ...checklist, hidden: true })
  }

  return (
    <List
      label="Getting started"
      secondaryDisplay={
        <GroupIconAction
          label="Dismiss checklist"
          icon={<X />}
          isLoading={isProcessing}
          onClick={handleHide}
        />
      }
      items={[
        {
          key: 'fund',
          content: <StepLabel>Fund your treasury</StepLabel>,
          meta: isFunded ? <StepDone /> : undefined,
        },
        {
          key: 'add-teammate',
          content: (
            <StepLabel>
              {getTeammateStepLabel({ isTeammateAdded, hasPendingInvitation })}
            </StepLabel>
          ),
          meta: hasPendingInvitation ? (
            <StepMeta>{WAITING_FOR_INVITATION}</StepMeta>
          ) : (
            getTeammateStepMeta({
              isTeammateAdded,
              hasPendingInvitation,
              isOwner,
              openInviteDialog,
            })
          ),
        },
        {
          key: 'auto-earn',
          content: <StepLabel>Earn on idle cash</StepLabel>,
          meta: isAutoEarnOn ? (
            <StepDone />
          ) : (
            <WorkspaceAutoEarnStepAction
              pendingAction={() => <StepMeta>Waiting for approval</StepMeta>}
            />
          ),
        },
      ]}
    />
  )
}
